<template>
    <div class="header navbar">
        <div class="container-fluid d-flex justify-content-end">
            <div id="product-toolbar-container" class="product-toolbar-container">
                <div class="product-toolbar d-flex align-items-center pb-3">
                    <div class="product-item-wrapper" v-for="product in productstoolbar" :key="product.product_id">
                        <button class="product-icon-button" :class="{ 'is-default': product.is_default }"
                            data-bs-toggle="tooltip" data-bs-placement="bottom" :title="product.application"
                            @click="redirectToProduct(product.login_url)">
                            <img v-if="product.logo" :src="product.logo" :alt="product.application + ' Logo'"
                                class="product-logo-image" />
                            <span v-else class="logo-fallback">{{
                                product.application?.substring(0, 1)
                            }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ProductToolbar',
    data() {
        return {
            productstoolbar: [],
        }
    },

    async mounted() {
        this.init();
    },

    methods: {
        redirectToProduct(url) {
            const finalUrl = `${url}&org_selected=${this.orgID}`;
            window.open(finalUrl, '_blank');
        },

        async getData() {
            if (!this.orgID || this.orgID === 'undefined' || this.orgID === 'null') {
                return null;
            }

            const authApiBaseUrl = process.env.VUE_APP_AUTH_API_BASE_URL;
            let url;
            if (this.isSuperAdmin || this.isOrgAdmin || this.isAnyProductAdmin) {
                url = `${authApiBaseUrl}/orgadmin/organisations/${this.orgID}/product-toolbar`;
            } else {
                const userID = this.$store.state.auth.jwtPayload?.user_id;
                if (!userID) return null;
                url = `${authApiBaseUrl}/users/${userID}/product-toolbar`;
            }

            try {
                const response = await this.$axios.get(url);
                if (response.data.success) {
                    return response.data.toolbar_products;
                }
                return null;
            } catch (error) {
                return null;
            }
        },

        async init() {
            const products = await this.getData();
            if (products) {
                this.filterProducts(products);
            }
        },

        filterProducts(products) {
            if (!Array.isArray(products)) {
                this.productstoolbar = [];
                return;
            }

            const isAdmin = this.isSuperAdmin || this.isOrgAdmin || this.isAnyProductAdmin;

            let productToolbar;

            if (isAdmin) {
                const seenApplications = new Set();
                productToolbar = products.filter((item) => {
                    const appName = item.application?.toLowerCase();
                    if (
                        !appName ||
                        seenApplications.has(appName) ||
                        appName == 'core'
                    ) {
                        return false
                    } else {
                        seenApplications.add(appName)
                        return true
                    }
                })
            } else {
                productToolbar = products.filter(
                    (item) => item.application?.toLowerCase() !== 'core',
                )
            }

            this.productstoolbar = productToolbar.map(product => {
                // Special handling to store the pentester URL.
                if (!localStorage.getItem('pentester_url') && product.login_url && product.login_url.includes('pentester')) {
                    localStorage.setItem('pentester_url', product.login_url + '?access_token=' + this.$store.state.auth.accessToken);
                }
                return {
                    product_id: product.product_id,
                    application: product.application,
                    is_default: product.is_default,
                    logo: product.logo,
                    icon: product.icon,
                    login_url: `${product.login_url}?access_token=${this.$store.state.auth.accessToken}`
                };
            });
        },
    },
    computed: {
        orgID() {
            const org = this.$store.state.user?.organisation || {};
            const orgId = this.$store.state.auth.isSuperAdmin || this.$helper?.isVertexAnalyst?.()
                ? (org.id || org.organisation_id)
                : (org.organisation_id || org.id);
            return (orgId && orgId !== 'undefined' && orgId !== 'null') ? orgId : (this.$helper?.setOrganisationID?.() || null);
        },
        isSuperAdmin() {
            return this.$store.state.auth.isSuperAdmin;
        },
        isOrgAdmin() {
            return this.$store.state.auth.isOrganisationAdmin;
        },
        userProducts() {
            return this.$store.state.auth.jwtPayload?.organisation_role?.[0]?.products || [];
        },
        isAnyProductAdmin() {
            // Assuming each product in userProducts has a `role_name` property that includes 'admin' for admin roles.
            return this.userProducts.some(p => p.role_name && p.role_name.toLowerCase().includes('admin'));
        },
        userIsProductAdmin() {
            return (productName) => {
                const product = this.userProducts.find(p => p.product.toLowerCase() === productName.toLowerCase());
                // Assuming each product in userProducts has a `role_name` property that includes 'admin' for admin roles.
                return !!(product && product.role_name && product.role_name.toLowerCase().includes('admin'));
            };
        }
    },
    watch: {
        orgID() {
            this.init();
        }
    },
}
</script>

<style scoped>
.header {
    top: 0;
    position: absolute;
    height: 66px;
    width: 100%;
    z-index: 1;
    background-color: #00a0c1;
}

/* Product toolbar styles */
.product-toolbar-container {
    padding: 3px;
}

.product-toolbar {
    min-height: 56px;
    position: relative;
    gap: 8px;
}

.product-icon-button {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    border: 2px solid #ffffff;
    background-color: #ffffff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    padding: 0;
    transition: transform 0.2s ease;
}

.product-icon-button:hover {
    transform: scale(1.1);
    z-index: 10;
}

.product-icon-button.is-default {
    border-color: #f3994d; /* Orange active border */
}

.product-logo-image {
    width: 100%;  /* Fill the entire button space */
    height: 100%;
    object-fit: cover; /* Ensures the image covers the circle completely */
    display: block; /* Removes any default inline spacing */
}

.logo-fallback {
    width: 100%;
    height: 100%;
    background-color: #333;
    color: #fff;
    font-size: 18px; /* Bigger text for the bigger button */
    font-weight: bold;
    display: flex;
    justify-content: center;
    align-items: center;
}

/* Bootstrap classes for flexbox since it might not be globally available */
.d-flex {
    display: flex !important;
}

.align-items-center {
    align-items: center !important;
}

.justify-content-end {
    justify-content: flex-end !important;
}
</style>